import { FC, Fragment, useReducer } from 'react';

import { useFormik } from 'formik';
import { Gem } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { PayOutDiamondsForm } from '../form';

import { userService } from 'v2/api/userService';
import { handleHttpError } from 'v2/utils';

interface PayOutDiamondsButtonProps {
  onSuccess: VoidFunction;
}

export const PayOutDiamondsButton: FC<PayOutDiamondsButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger, isMutating } = useSWRMutation('payOutDiamondsForAll', userService.payOutDiamondsForAll, {
    onSuccess: () => {
      onSuccess();
      toast.success('Diamonds are credited to all users');
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<PayOutDiamondsForm>({
    initialValues: { amount: '' },
    onSubmit: (values) => {
      trigger(Number(values.amount));
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setOpen();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Gem width={20} height={20} />} variant="contained" size="large">
        Pay Out Diamonds
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Pay Out Diamonds For All Users</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              name="amount"
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Amount"
              type="number"
              fullWidth
              required
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Pay Out
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
